/** @jsxImportSource solid-js */
import { createSignal } from "solid-js";
import { createSelectInput } from "#palette/components/form/select.solid";
import { createTextInput } from "#palette/components/form/text.solid";
import { createCheckboxInput } from "#root/lib/palette/components/form/checkbox.solid.js";
import { SubmitButton } from "#root/lib/palette/components/form/submit.solid.js";
import { Spinner } from "#root/lib/palette/components/spinner.solid.js";
import { Icons } from "#root/lib/palette/components/icons.solid.js";
import { TooltipBase } from "#root/lib/palette/components/tooltip.solid.js";

const question1Options = [
  "What was the full name of the first school you attended?",
  "To what city did you go the first time you flew on a plane?",
  "What was the destination of your most memorable childhood vacation?",
];

const question2Options = [
  "What is your oldest sibling's middle name?",
  "What was the surname of your teacher in your first year of school?",
  "What was the name of your first stuffed animal?",
];

const question3Options = [
  "What was the surname of your first boss?",
  "What was your childhood best friend's nickname?",
  "What was the name of your childhood best friend's pet?",
];

export function RecoverySetupForm(props: {
  goNext: (
    selectedQuestions: string[],
    givenAnswers: string[],
    consentBroadcast: boolean,
  ) => Promise<void>;
}) {
  const [loading, setLoading] = createSignal(false);

  const question1 = createSelectInput({
    items: question1Options.map((i, idx) => {
      return { id: idx.toString(), display: i };
    }),
  });
  const question1Answer = createTextInput({});
  const question2 = createSelectInput({
    items: question2Options.map((i, idx) => {
      return { id: idx.toString(), display: i };
    }),
  });
  const question2Answer = createTextInput({});
  const question3 = createSelectInput({
    items: question3Options.map((i, idx) => {
      return { id: idx.toString(), display: i };
    }),
  });
  const question3Answer = createTextInput({});

  const consentBroadcast = createCheckboxInput(
    <>
      <strong>(Optional)</strong> I consent to receiving emails requesting
      anonymous responses to surveys or other feedback (approximately 2 per
      year).
    </>,
    true,
  );
  let formElement!: HTMLFormElement;

  function tryFinalizeAccount() {
    formElement.setAttribute("data-validate", "false");
    setTimeout(async () => {
      if (
        question1.selected() === -1 ||
        question2.selected() === -1 ||
        question3.selected() === -1 ||
        question1Answer.value() === "" ||
        question2Answer.value() === "" ||
        question3Answer.value() === ""
      ) {
        formElement.setAttribute("data-validate", "true");
        return;
      }

      const selectedQuestions = [
        question1Options[question1.selected()],
        question2Options[question2.selected()],
        question3Options[question3.selected()],
      ];
      const givenAnswers = [
        question1Answer.value(),
        question2Answer.value(),
        question3Answer.value(),
      ];

      setLoading(true);
      await props
        .goNext(selectedQuestions, givenAnswers, consentBroadcast.value())
        .finally(() => {
          setLoading(false);
        });
    }, 100);
  }

  return (
    <form class="group" data-validate="false" ref={formElement}>
      <span>
        <h1 class="mb-3 mt-6 text-xl font-bold text-brand-900 font-serif inline">
          Setup Account Recovery
        </h1>
        <span class="relative group/tooltip ml-2">
          <Icons.CircleInfo />
          <TooltipBase>
            <p class="py-2">
              These security questions allow us to validate your identity if you
              need to recover your account in the future.
            </p>
            <p class="py-2">
              The best secret question answers have four characteristics. They
              are:
            </p>
            <ol class="list-circle-inside py-2">
              <li>
                <b>Safe</b> (The answers cannot be easily researched or
                guessed.)
              </li>
              <li>
                <b>Stable</b> (The answer will not change over time.)
              </li>
              <li>
                <b>Memorable</b> (If you forget the answer, the question does
                not work as it should.)
              </li>
              <li>
                <b>Simple and specific</b> (Something complex or vague may be
                too difficult to remember.)
              </li>
            </ol>
            <p class="py-2">
              It is not always easy to meet all 4 characteristics, but at least
              keep the following in mind:
            </p>
            <ol class="list-circle-inside py-2">
              <li>
                Avoid picking answers that are well known to your friends,
                shared on social media, or common knowledge.
              </li>
              <li>
                Avoid picking answers that can be easily found using a search
                engine such as Google.
              </li>
              <li>
                Avoid picking answers that you may not be able to remember
                later.
              </li>
              <li>
                Avoid really short answers if possible. (A good minimum limit is
                8 characters.)
              </li>
            </ol>
            <p class="py-2">
              The very best answers have no obvious relationship to the question
              but that you will be able to remember. If you are not sure if you
              will remember your answers, use a password manager to store them.
            </p>
            <p class="font-bold py-2">
              Answers to security questions are case sensitive!
            </p>
          </TooltipBase>
        </span>
      </span>
      <div>
        {question1.component}
        {question1Answer.component}
        {question2.component}
        {question2Answer.component}
        {question3.component}
        {question3Answer.component}
      </div>
      {consentBroadcast.component}
      <SubmitButton
        onClick={tryFinalizeAccount}
        disabled={false}
        title={"Continue"}
      />
      {loading() && <Spinner />}
    </form>
  );
}
